'use client';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import { animated, useSpring, useSpringRef, type SpringValue } from 'react-spring';
import {
    Flödesspår,
    GRUND,
    Gradientfält,
    Pulsnod,
    SPRING,
    Siffervisare,
    TYPSNITT,
    TextAvtäckning,
    hexTillRgba,
    ljus,
    useSekvens,
    type ScenKontext
} from 'src/components/rörelse';
import { PresentationsNav } from '../komponenter/PresentationsNav';
import { ScenSektion } from '../komponenter/ScenSektion';
import { hittaPresentation } from '../presentationer';
import { LEDAMÖTER, PROTOKOLLRADER, TJÄNSTER, ÄMNEN } from './styrelsen-innehåll';

const AnimeradDiv = animated('div');
const KOMBO = 'blå' as const;
/** Sidan går blå → lila. Mötet börjar i styrelsens färg och slutar i händelsernas. */
const SLUTKOMBO = 'lila' as const;

export function StyrelsenView() {
    const presentation = hittaPresentation('styrelsen');
    const [glödIndex, sättGlödIndex] = useState(0);
    const glöd = useCallback(
        (index: number) => (synlig: boolean) => {
            if (synlig) sättGlödIndex(index);
        },
        []
    );

    return (
        <>
            <Gradientfält
                kombinationer={[KOMBO, KOMBO, KOMBO, SLUTKOMBO, SLUTKOMBO, SLUTKOMBO]}
                positioner={[
                    [24, 26],
                    [70, 34],
                    [36, 58],
                    [66, 54],
                    [40, 72],
                    [60, 38]
                ]}
                index={glödIndex}
                styrka={0.4}
            />

            <ScenSektion
                id="kallelse"
                kombo={KOMBO}
                nivå="h1"
                rubrik={presentation.titel}
                brödtext={[
                    presentation.ingress,
                    'Kallelsen bär tid, plats och dagordning, och lägger sig i ledamöternas kalendrar av sig själv. Flyttas mötet flyttar posten med; ställs det in försvinner den.'
                ]}
                onSynlig={glöd(0)}
            >
                <Kallelse />
            </ScenSektion>

            <ScenSektion
                id="amnen"
                kombo={KOMBO}
                spegla
                rubrik="Ämnena samlas in före mötet"
                brödtext={[
                    'Den som kommer på något en tisdag i september ska inte behöva minnas det till nästa möte. Ledamöterna lägger till sina punkter löpande, och dagordningen är färdig när mötet börjar.'
                ]}
                onSynlig={glöd(1)}
            >
                {({ framsteg }: ScenKontext) => <Ämneslista framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="motet"
                kombo={KOMBO}
                rubrik="Mötet hålls där ledamöterna är"
                brödtext={[
                    'Ett videomötesrum hör till mötet, inte till en länk någon klistrat in i ett mejl. Under mötet skrivs protokollet i samma vy som dagordningen, och en transkription kan läsas in som underlag.'
                ]}
                onSynlig={glöd(2)}
            >
                <Deltagare />
            </ScenSektion>

            <ScenSektion
                id="signering"
                kombo={SLUTKOMBO}
                spegla
                rubrik="Signaturerna kommer i ordning"
                brödtext={[
                    'Protokollet delas med de ledamöter som ska skriva under. Var och en legitimerar sig med BankID, och signaturen knyts till det personnummer legitimeringen gav — inte till ett namn någon skrivit in.',
                    'När den sista är inne låses protokollet.'
                ]}
                onSynlig={glöd(3)}
            >
                {({ synlig }: ScenKontext) => <Signeringskedja aktiv={synlig} />}
            </ScenSektion>

            <ScenSektion
                id="protokoll"
                kombo={SLUTKOMBO}
                rubrik="Och protokollet skriver ut sig självt"
                brödtext={[
                    'Beslutstexten blir en PDF med föreningens uppgifter, paragrafnumrering och signaturerna längst ned.',
                    'Den färdiga handlingen hamnar i föreningens Drive-mapp under rätt år, så att nästa styrelse hittar den utan att fråga någon.'
                ]}
                onSynlig={glöd(4)}
            >
                <Protokoll />
            </ScenSektion>

            <ScenSektion
                id="arkiv"
                kombo={SLUTKOMBO}
                spegla
                höjd="auto"
                rubrik="Fyra tjänster, ett möte"
                brödtext={[
                    'Kalender, mötesrum, arkiv och utskick är fyra olika Google-tjänster. För styrelsen ska de vara ett enda möte som går att slå upp i efterhand.'
                ]}
                onSynlig={glöd(5)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <Flödesspår
                        noder={TJÄNSTER}
                        kombo={SLUTKOMBO}
                        riktning="vågrät"
                        etikett="Tjänster som ett styrelsemöte rör"
                    />
                    <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        <Nyckeltal etikett="Protokoll i arkivet" värde={38} />
                        <Nyckeltal etikett="Fattade beslut" värde={214} />
                    </Box>
                </Box>
            </ScenSektion>

            <PresentationsNav nuvarande="styrelsen" />
        </>
    );
}

function Kallelse() {
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 400,
                p: 3,
                borderRadius: 2,
                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.34)}`,
                bgcolor: hexTillRgba('#12151F', 0.8)
            }}
        >
            <Box sx={{ fontSize: 13, color: GRUND.dämpad, mb: 0.75 }}>Torsdag 24 september, 18.30</Box>
            <Box
                sx={{
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 48",
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    letterSpacing: '-0.022em',
                    lineHeight: 1.15,
                    color: GRUND.text,
                    mb: 2.5
                }}
            >
                Styrelsemöte nr 7
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {LEDAMÖTER.map((l) => (
                    <Box key={l.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 13.5 }}>
                        <Box sx={{ color: GRUND.text }}>{l.namn}</Box>
                        <Box sx={{ color: GRUND.dämpad }}>{l.roll}</Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function Ämneslista({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 460 }}>
            {ÄMNEN.map((ämne, i) => {
                const tröskel = i / ÄMNEN.length;
                return (
                    <AnimeradDiv
                        key={ämne.id}
                        style={{
                            opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.22)),
                            transform: framsteg.to((p) => `translateY(${p >= tröskel ? 0 : 20}px)`),
                            willChange: 'transform, opacity'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                gap: 2,
                                py: 1.6,
                                borderBottom: `1px solid ${GRUND.kant}`
                            }}
                        >
                            <Box sx={{ fontSize: 14.5, color: GRUND.text }}>{ämne.rubrik}</Box>
                            <Box sx={{ fontSize: 12.5, color: GRUND.dämpad, whiteSpace: 'nowrap' }}>{ämne.av}</Box>
                        </Box>
                    </AnimeradDiv>
                );
            })}
        </Box>
    );
}

function Deltagare() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {LEDAMÖTER.slice(0, 3).map((l, i) => (
                <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Pulsnod kombo={KOMBO} storlek={12} tänd={i === 0} />
                    <Box sx={{ fontSize: 14.5, color: i === 0 ? GRUND.text : GRUND.dämpad }}>{l.namn}</Box>
                    {i === 0 ? (
                        <Box sx={{ fontSize: 12, color: ljus(KOMBO) }}>talar</Box>
                    ) : null}
                </Box>
            ))}
        </Box>
    );
}

/**
 * Signaturerna kedjas med useSekvens i stället för att bara fördröjas var för sig.
 * Poängen är att fjärde signaturen inte ska kunna dyka upp före den tredje — det är
 * BankID-signeringens grammatik, och animationen ska inte kunna motsäga den.
 */
function Signeringskedja({ aktiv }: { aktiv: boolean }) {
    const ref1 = useSpringRef();
    const ref2 = useSpringRef();
    const ref3 = useSpringRef();
    const ref4 = useSpringRef();
    const refs = [ref1, ref2, ref3, ref4];

    const s1 = useSpring({ ref: ref1, opacity: aktiv ? 1 : 0.25, config: SPRING.snärtig });
    const s2 = useSpring({ ref: ref2, opacity: aktiv ? 1 : 0.25, config: SPRING.snärtig });
    const s3 = useSpring({ ref: ref3, opacity: aktiv ? 1 : 0.25, config: SPRING.snärtig });
    const s4 = useSpring({ ref: ref4, opacity: aktiv ? 1 : 0.25, config: SPRING.snärtig });
    const fjädrar = [s1, s2, s3, s4];

    useSekvens(refs, [0, 0.35, 0.7, 1.05], aktiv);

    return (
        <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {LEDAMÖTER.map((l, i) => (
                <AnimeradDiv key={l.id} style={fjädrar[i]}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderRadius: 1.5,
                            border: `1px solid ${hexTillRgba(ljus(SLUTKOMBO), 0.4)}`,
                            bgcolor: hexTillRgba('#12151F', 0.72)
                        }}
                    >
                        <Box>
                            <Box sx={{ fontSize: 14.5, color: GRUND.text }}>{l.namn}</Box>
                            <Box sx={{ fontSize: 12, color: GRUND.dämpad, mt: 0.2 }}>{l.roll}</Box>
                        </Box>
                        <Box sx={{ fontSize: 12.5, color: ljus(SLUTKOMBO), whiteSpace: 'nowrap' }}>
                            Signerad med BankID
                        </Box>
                    </Box>
                </AnimeradDiv>
            ))}
        </Box>
    );
}

function Protokoll() {
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 440,
                p: 3,
                borderRadius: 2,
                border: `1px solid ${hexTillRgba(ljus(SLUTKOMBO), 0.3)}`,
                bgcolor: hexTillRgba('#12151F', 0.8)
            }}
        >
            <TextAvtäckning
                text={PROTOKOLLRADER}
                enhet="rad"
                avstånd={10}
                sx={{
                    fontSize: 13.5,
                    lineHeight: 2.05,
                    color: GRUND.dämpad,
                    fontVariantNumeric: 'tabular-nums'
                }}
            />
        </Box>
    );
}

function Nyckeltal({ etikett, värde }: { etikett: string; värde: number }) {
    return (
        <Box>
            <Siffervisare
                till={värde}
                variant="h2"
                component="span"
                sx={{
                    display: 'block',
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 96",
                    fontWeight: 600,
                    fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
                    lineHeight: 1,
                    letterSpacing: '-0.035em',
                    color: ljus(SLUTKOMBO)
                }}
            />
            <Box sx={{ fontSize: 13.5, color: GRUND.dämpad, mt: 0.75 }}>{etikett}</Box>
        </Box>
    );
}
